import { memoize } from 'lodash/fp';
import { KeyboardEvent } from 'react';
import { InputValue } from '@optimus-web/core';
import { SelectionStrategy, TreeOption, TreeProps } from './types';
import { getTextContentOfNode, toggleEntrySelection, toggleGroupSelection } from './utils';

export type OptionProps = Pick<TreeOption, 'label' | 'icon' | 'tagProps' | 'id' | 'description' | 'disabled'> & {
    offset?: number;
    isGroup?: boolean;
    showDivider?: boolean;
    optionsCount?: number;
    checked?: boolean;
    indeterminate?: boolean;
    onChange: (c: boolean) => void;
    onKeyDown: (e: KeyboardEvent<HTMLLIElement>) => void;
} & ({ options: OptionProps[]; value?: string } | { value: string });

const mapTreeOptionToOptionProps = (
    option: TreeOption,
    offset: number,
    handleChange: (val: string[]) => void,
    onKeyDown: (e: KeyboardEvent<HTMLLIElement>) => void,
    value: Set<string>,
    multiple = false,
    selectionStrategy: SelectionStrategy,
    parentDisabled = false
): OptionProps => {
    const isGroup = 'options' in option;
    const hasValue = 'value' in option;

    const effectiveDisabled = option.disabled || parentDisabled;

    return {
        isGroup,
        offset,
        tagProps: option.tagProps,
        label: option.label,
        icon: option.icon,
        id: option.id,
        description: option.description,
        disabled: effectiveDisabled,
        onChange: (c: boolean) => {
            if (effectiveDisabled) {
                return;
            }

            if (multiple) {
                handleChange(
                    isGroup
                        ? toggleGroupSelection(option, c, value, selectionStrategy)
                        : toggleEntrySelection(option.value, c, value)
                );
            } else {
                handleChange(hasValue ? [option.value] : []);
            }
        },
        onKeyDown,
        ...(isGroup
            ? { options: [] as OptionProps[], ...(hasValue && { value: option.value }) }
            : { value: option.value }),
        checked: hasValue && value.has(option.value),
    };
};

const mapTreeOptionsToEntryProps = memoize(
    ({
        options,
        value,
        searchQuery,
        multiple,
        handleChange,
        handleKeyDown,
        selectionStrategy,
    }: Pick<TreeProps, 'options' | 'multiple'> & {
        value: Set<string>;
        searchQuery?: InputValue;
        handleChange: (val: string[]) => void;
        handleKeyDown: (e: KeyboardEvent<HTMLLIElement>) => void;
        selectionStrategy: SelectionStrategy;
    }) => {
        const searchRegex = new RegExp(String(searchQuery), 'i');

        const mapOption = (
            option: TreeOption,
            offset = 0,
            hasMatchingParent = false,
            parentDisabled = false
        ): OptionProps | null => {
            const newTreeOption = mapTreeOptionToOptionProps(
                option,
                offset,
                handleChange,
                handleKeyDown,
                value,
                multiple,
                selectionStrategy,
                parentDisabled
            );

            const isGroup = 'options' in option && 'options' in newTreeOption;
            let optionsCount = 0;

            const matchesParent = searchQuery
                ? hasMatchingParent || searchRegex.test(getTextContentOfNode(option.label))
                : true;

            if (isGroup) {
                let allDescendantsDisabled = true;

                for (const child of option.options) {
                    const childMapped = mapOption(child, offset + 1, matchesParent, newTreeOption.disabled);
                    if (childMapped) {
                        newTreeOption.options.push(childMapped);
                        optionsCount += childMapped.optionsCount || 1;

                        if (!childMapped.disabled) {
                            allDescendantsDisabled = false;
                        }
                    }
                }

                newTreeOption.options = newTreeOption.options.filter((opt) => opt && (opt.optionsCount || 1) > 0);

                newTreeOption.optionsCount = optionsCount;

                // Disable group if all descendant children are disabled
                if (allDescendantsDisabled && selectionStrategy === 'children-only') {
                    newTreeOption.disabled = true;
                }

                if (selectionStrategy !== 'check-strictly') {
                    // only aggregate state in non check-strictly mode
                    const enabledOptions = newTreeOption.options.filter((opt) => !opt.disabled);

                    newTreeOption.checked = enabledOptions.length > 0 && enabledOptions.every((opt) => opt.checked);
                    if (!newTreeOption.checked) {
                        newTreeOption.indeterminate = enabledOptions.some((opt) => opt.checked || opt.indeterminate);
                    }
                }
            }

            return matchesParent || (newTreeOption.optionsCount || 0) > 0 ? newTreeOption : null;
        };

        return options.map((opt) => mapOption(opt)).filter((opt): opt is OptionProps => Boolean(opt));
    }
);

export const useOptions = ({
    options,
    value,
    searchQuery,
    multiple,
    handleChange,
    handleKeyDown,
    selectionStrategy,
}: Pick<TreeProps, 'options' | 'value' | 'multiple'> & {
    searchQuery?: InputValue;
    handleChange: (val: string[]) => void;
    handleKeyDown: (e: KeyboardEvent<HTMLLIElement>) => void;
    selectionStrategy: SelectionStrategy;
}) =>
    mapTreeOptionsToEntryProps({
        options,
        value: new Set(value),
        searchQuery,
        multiple,
        handleChange,
        handleKeyDown,
        selectionStrategy,
    });
